import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Mail, Phone, MapPin, Shield, CreditCard, Bell, Globe, DollarSign, Lock, Eye, FileText, AlertTriangle, ChevronRight, CheckCircle, XCircle, Navigation } from 'lucide-react';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
interface AccountSettingsProps {
  onBack: () => void;
}
export function AccountSettings({
  onBack
}: AccountSettingsProps) {
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [browsingRadius, setBrowsingRadius] = useState(10); // Default 10 miles
  const [notifications, setNotifications] = useState({
    push: true,
    email: true,
    sms: false
  });
  const sections = [{
    title: 'Personal Information',
    items: [{
      icon: User,
      label: 'Full Name',
      value: 'John Doe',
      action: 'Edit'
    }, {
      icon: Mail,
      label: 'Email',
      value: 'john.doe@example.com',
      action: 'Edit'
    }, {
      icon: Phone,
      label: 'Phone',
      value: '+1 (555) 123-4567',
      action: 'Edit'
    }, {
      icon: MapPin,
      label: 'Address',
      value: 'San Francisco, CA',
      action: 'Edit'
    }]
  },
  // TODO: Verification section - hidden for now, will be a future feature
  // {
  //   title: 'Verification',
  //   items: [
  //     {
  //       icon: Shield,
  //       label: 'Identity Verification',
  //       value: 'Verified',
  //       status: 'success',
  //     },
  //     {
  //       icon: Phone,
  //       label: 'Phone Verification',
  //       value: 'Verified',
  //       status: 'success',
  //     },
  //     {
  //       icon: CreditCard,
  //       label: 'Payment Method',
  //       value: 'Visa •••• 4242',
  //       action: 'Update',
  //     },
  //   ],
  // },
  {
    title: 'Preferences',
    items: [{
      icon: Globe,
      label: 'Language',
      value: 'English (US)',
      action: 'Change'
    }, {
      icon: DollarSign,
      label: 'Currency',
      value: 'USD ($)',
      action: 'Change'
    }, {
      icon: Navigation,
      label: 'Browsing Radius',
      value: `${browsingRadius} miles`,
      subtitle: 'Show listings within this distance',
      action: 'Change'
    }]
  }, {
    title: 'Privacy & Security',
    items: [{
      icon: Lock,
      label: 'Change Password',
      action: 'Update'
    }, {
      icon: Shield,
      label: 'Two-Factor Authentication',
      value: 'Off',
      action: 'Enable'
    }, {
      icon: Eye,
      label: 'Profile Visibility',
      value: 'Public',
      action: 'Manage'
    }]
  }, {
    title: 'Legal',
    items: [{
      icon: FileText,
      label: 'Terms of Service',
      action: 'View'
    }, {
      icon: FileText,
      label: 'Privacy Policy',
      action: 'View'
    }, {
      icon: FileText,
      label: 'Community Guidelines',
      action: 'View'
    }]
  }];
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-547">
      {/* Header */}
      <div className="bg-white border-b border-sand sticky top-0 z-10" data-id="element-548">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3" data-id="element-549">
          <div className="flex items-center gap-3" data-id="element-550">
            <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-sand/20 transition-colors" data-id="element-551">
              <ArrowLeft size={24} className="text-charcoal" data-id="element-552" />
            </button>
            <h1 className="text-xl font-bold text-charcoal" data-id="element-553">
              Account Settings
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-8" data-id="element-554">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8" data-id="element-555">
          {sections.map((section, idx) => <motion.div key={idx} variants={item} className="space-y-3" data-id="element-556">
              <h2 className="text-sm font-bold text-moss-gray uppercase tracking-wider px-1" data-id="element-557">
                {section.title}
              </h2>
              <div className="bg-white rounded-ios-lg shadow-ios overflow-hidden" data-id="element-558">
                {section.items.map((row, rowIdx) => {
              const Icon = row.icon;
              return <div key={rowIdx} className={`flex items-center justify-between p-4 ${rowIdx !== section.items.length - 1 ? 'border-b border-sand' : ''} hover:bg-sand/5 transition-colors cursor-pointer`} data-id="element-559">
                      <div className="flex items-center gap-3" data-id="element-560">
                        <div className="w-8 h-8 rounded-full bg-sand/20 flex items-center justify-center text-charcoal" data-id="element-561">
                          <Icon size={16} data-id="element-562" />
                        </div>
                        <div data-id="element-563">
                          <div className="text-sm font-medium text-charcoal" data-id="element-564">
                            {row.label}
                          </div>
                          {row.value && <div className="text-xs text-charcoal/60" data-id="element-565">
                              {row.value}
                            </div>}
                          {row.subtitle && <div className="text-xs text-charcoal/50" data-id="element-566">
                              {row.subtitle}
                            </div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-567">
                        {row.status === 'success' && <CheckCircle size={16} className="text-green-500" data-id="element-568" />}
                        {row.action && <span className="text-xs font-medium text-dark-green" data-id="element-569">
                            {row.action}
                          </span>}
                        <ChevronRight size={16} className="text-charcoal/40" data-id="element-570" />
                      </div>
                    </div>;
            })}
              </div>
            </motion.div>)}

          {/* Danger Zone */}
          <motion.div variants={item} className="space-y-3" data-id="element-571">
            <h2 className="text-sm font-bold text-red-500 uppercase tracking-wider px-1" data-id="element-572">
              Danger Zone
            </h2>
            <div className="bg-white rounded-ios-lg shadow-ios overflow-hidden border border-red-100" data-id="element-573">
              <button onClick={() => setShowDeactivateModal(true)} className="w-full flex items-center justify-between p-4 hover:bg-red-50 transition-colors text-left" data-id="element-574">
                <div className="flex items-center gap-3" data-id="element-575">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-500" data-id="element-576">
                    <AlertTriangle size={16} data-id="element-577" />
                  </div>
                  <div data-id="element-578">
                    <div className="text-sm font-medium text-red-600" data-id="element-579">
                      Deactivate Account
                    </div>
                    <div className="text-xs text-red-400" data-id="element-580">
                      Temporarily disable your account
                    </div>
                  </div>
                </div>
                <ChevronRight size={16} className="text-red-300" data-id="element-581" />
              </button>
            </div>
          </motion.div>

          <div className="text-center text-xs text-charcoal/40 pt-4 pb-8" data-id="element-582">
            Version 1.0.0 (Build 2023.12.15)
          </div>
        </motion.div>
      </div>

      <Modal isOpen={showDeactivateModal} onClose={() => setShowDeactivateModal(false)} title="Deactivate Account" data-id="element-583">
        <div className="space-y-4" data-id="element-584">
          <div className="bg-red-50 p-4 rounded-ios border border-red-100" data-id="element-585">
            <p className="text-sm text-red-700" data-id="element-586">
              This will temporarily hide your profile and listings. You can
              reactivate your account at any time by logging back in.
            </p>
          </div>
          <p className="text-charcoal text-sm" data-id="element-587">
            Please enter your password to confirm deactivation:
          </p>
          <Input type="password" placeholder="Enter password" value="" onChange={() => {}} data-id="element-588" />
          <div className="flex gap-3 pt-2" data-id="element-589">
            <Button variant="secondary" onClick={() => setShowDeactivateModal(false)} className="flex-1" data-id="element-590">
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setShowDeactivateModal(false)} className="flex-1 bg-red-500 hover:bg-red-600 border-red-500" data-id="element-591">
              Deactivate
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}